/**
 * 简化固件升级协议（Dongle / 耳机）
 * 对齐 111/firmwareUpgrade.js & C++ HidWorker.cpp
 */

import type { UpgradeCallbacks } from './firmware-types'

const PACKET_SIZE = 64
const DATA_START_INDEX = 8
const FIRMWARE_OFFSET = 0x3100

const HEADER_0 = 0xaa
const HEADER_1 = 0x55

const CMD = {
  HANDSHAKE: 0x11,
  ACK_DATA: 0x81,
  UPGRADE_END: 0x82,
} as const

const STATE = {
  IDLE: 0,
  HANDSHAKE: 1,
  TRANSFERRING: 2,
  COMPLETING: 3,
  DONE: 4,
  ERROR: -1,
} as const

let device: HIDDevice | null = null
let firmwareBuffer: Uint8Array | null = null
let fileReadAddr = FIRMWARE_OFFSET
let currentState: number = STATE.IDLE
let progressCallback: UpgradeCallbacks['onProgress'] = null
let completeCallback: UpgradeCallbacks['onComplete'] = null
let errorCallback: UpgradeCallbacks['onError'] = null
let inputReportHandler: ((e: HIDInputReportEvent) => void) | null = null

let outputReportId = 0
let useFeatureReport = false

function buildPacket(): Uint8Array {
  return new Uint8Array(PACKET_SIZE)
}

function isValidHeader(data: Uint8Array): boolean {
  return data[0] === HEADER_0 && data[1] === HEADER_1
}

function calcCrc16Ccitt(init: number, bytes: Uint8Array): number {
  let crc = init & 0xffff
  for (let i = 0; i < bytes.length; i++) {
    crc ^= (bytes[i] & 0xff) << 8
    for (let b = 0; b < 8; b++) {
      if (crc & 0x8000) {
        crc = ((crc << 1) ^ 0x1021) & 0xffff
      } else {
        crc = (crc << 1) & 0xffff
      }
    }
  }
  return crc & 0xffff
}

function buildResultValueByte(resultCode: number, hasPendingDevice: boolean): number {
  const code = Number(resultCode) & 0x0f
  const pendingNibble = hasPendingDevice ? 0x10 : 0x00
  return (pendingNibble | code) & 0xff
}

function inferUpdateFailedReasonByte(error: unknown): number {
  const msg = String(error instanceof Error ? error.message : error || '')
  const hasPendingDevice = !!(device && device.opened)
  if (/crc|校验/i.test(msg)) return buildResultValueByte(0x02, hasPendingDevice)
  if (/version|版本|low|过低/i.test(msg)) return buildResultValueByte(0x01, hasPendingDevice)
  return buildResultValueByte(0x01, hasPendingDevice)
}

async function sendUpdateFailedNotice(resultValueByte = 0x11): Promise<void> {
  if (!device?.opened) return
  const data = new Uint8Array([0xaa, 0x55, 0x00, 0x00, 0x82, 0x01, 0x00, resultValueByte & 0xff])
  const crc = calcCrc16Ccitt(0xffff, data.subarray(4, 8))
  data[2] = crc & 0xff
  data[3] = (crc >> 8) & 0xff
  if (useFeatureReport) {
    await device.sendFeatureReport(outputReportId, data)
  } else {
    await device.sendReport(outputReportId, data)
  }
}

function selectWritableReport(dev: HIDDevice): void {
  const outIds = new Set<number>()
  const featIds = new Set<number>()
  for (const col of dev.collections || []) {
    for (const r of col.outputReports || []) outIds.add(r.reportId)
    for (const r of col.featureReports || []) featIds.add(r.reportId)
  }
  const output = Array.from(outIds).sort((a, b) => a - b)
  const feature = Array.from(featIds).sort((a, b) => a - b)
  if (output.length) {
    outputReportId = output[0]
    useFeatureReport = false
    return
  }
  if (feature.length) {
    outputReportId = feature[0]
    useFeatureReport = true
    return
  }
  outputReportId = 0
  useFeatureReport = false
}

function getSendFileLength(): number {
  return firmwareBuffer?.length ?? 0
}

function getActualFirmwareSize(): number {
  if (!firmwareBuffer) return 0
  return firmwareBuffer.length - FIRMWARE_OFFSET
}

function updateProgress(message: string): void {
  const actualSize = getActualFirmwareSize()
  const sent = Math.min(fileReadAddr - FIRMWARE_OFFSET, actualSize)
  const percent = actualSize > 0 ? Math.min(Math.floor((sent / actualSize) * 100), 100) : 0
  progressCallback?.(percent, message)
}

function buildHandshakePacket(): Uint8Array {
  const packet = buildPacket()
  const len = getSendFileLength()
  packet[0] = HEADER_0
  packet[1] = HEADER_1
  packet[4] = CMD.HANDSHAKE
  packet[5] = 0x66
  packet[6] = len & 0xff
  packet[7] = (len >> 8) & 0xff
  packet[8] = (len >> 16) & 0xff
  packet[9] = (len >> 24) & 0xff
  return packet
}

function getTargetAddrFromPacket(data: Uint8Array): number {
  return (data[7] | (data[8] << 8) | (data[9] << 16) | (data[10] << 24)) >>> 0
}

function buildFirmwareDataPacket(): Uint8Array {
  const packet = buildPacket()
  packet[0] = HEADER_0
  packet[1] = HEADER_1
  packet[4] = CMD.ACK_DATA
  if (!firmwareBuffer) return packet
  for (let i = DATA_START_INDEX; i < PACKET_SIZE; i++) {
    if (fileReadAddr >= firmwareBuffer.length) {
      packet[i] = 0
    } else {
      packet[i] = firmwareBuffer[fileReadAddr]
    }
    fileReadAddr++
  }
  return packet
}

function buildEndPacket(): Uint8Array {
  const packet = buildPacket()
  packet[0] = HEADER_0
  packet[1] = HEADER_1
  packet[4] = CMD.UPGRADE_END
  return packet
}

function buildUpgradeEndAckPacket(): Uint8Array {
  return buildEndPacket()
}

async function sendPacketData(packet: Uint8Array): Promise<void> {
  if (!device?.opened) throw new Error('设备未连接')
  if (useFeatureReport) {
    await device.sendFeatureReport(outputReportId, packet)
  } else {
    await device.sendReport(outputReportId, packet)
  }
}

async function sendNextFirmwarePacket(): Promise<void> {
  if (!firmwareBuffer || fileReadAddr >= firmwareBuffer.length) {
    await sendPacketData(buildEndPacket())
    currentState = STATE.DONE
    cleanup()
    updateProgress('升级完成！')
    completeCallback?.()
    return
  }
  await sendPacketData(buildFirmwareDataPacket())
  const actualSize = getActualFirmwareSize()
  const sent = Math.min(fileReadAddr - FIRMWARE_OFFSET, actualSize)
  updateProgress(`传输中 ${sent}/${actualSize}`)
}

function handleInputReport(data: Uint8Array): void {
  if (!isValidHeader(data)) return
  const cmd = data[4]
  if (cmd === CMD.ACK_DATA) {
    if (currentState === STATE.HANDSHAKE || currentState === STATE.TRANSFERRING) {
      currentState = STATE.TRANSFERRING
      const targetAddr = getTargetAddrFromPacket(data)
      if (targetAddr >= FIRMWARE_OFFSET) {
        fileReadAddr = targetAddr
      }
      sendNextFirmwarePacket().catch(handleError)
    }
  } else if (cmd === CMD.UPGRADE_END) {
    sendPacketData(buildUpgradeEndAckPacket())
      .catch(() => {})
      .finally(() => {
        currentState = STATE.DONE
        cleanup()
        updateProgress('升级完成！')
        completeCallback?.()
      })
  }
}

function onInputReport(event: HIDInputReportEvent): void {
  handleInputReport(new Uint8Array(event.data.buffer))
}

async function handleError(error: unknown): Promise<void> {
  currentState = STATE.ERROR
  try {
    await sendUpdateFailedNotice(inferUpdateFailedReasonByte(error))
  } catch {
    /* best-effort failure notice, aligned with old project */
  }
  cleanup()
  const err = error instanceof Error ? error : new Error(String(error))
  errorCallback?.(err)
}

function cleanup(): void {
  if (device && inputReportHandler) {
    device.removeEventListener('inputreport', inputReportHandler)
  }
  inputReportHandler = null
}

/** 加载并填充固件文件（256 字节对齐） */
export async function loadFirmwareFile(file: File): Promise<Uint8Array> {
  const originalBuffer = new Uint8Array(await file.arrayBuffer())
  const originalSize = originalBuffer.length
  const yushu = (originalSize - 0x3100) % 256
  const shang = Math.floor((originalSize - 0x3100) / 256)
  const targetSize = yushu > 0 ? (shang + 1) * 256 + 0x3100 : shang * 256 + 0x3100
  const firmware = new Uint8Array(targetSize)
  firmware.set(originalBuffer)
  return firmware
}

export async function startSimpleUpgrade(
  hidDevice: HIDDevice,
  firmware: Uint8Array,
  callbacks: UpgradeCallbacks = {}
): Promise<void> {
  if (currentState !== STATE.IDLE && currentState !== STATE.ERROR && currentState !== STATE.DONE) {
    throw new Error('升级正在进行中')
  }
  device = hidDevice
  firmwareBuffer = firmware
  fileReadAddr = FIRMWARE_OFFSET
  currentState = STATE.HANDSHAKE
  progressCallback = callbacks.onProgress ?? null
  completeCallback = callbacks.onComplete ?? null
  errorCallback = callbacks.onError ?? null

  try {
    if (!device.opened) throw new Error('设备未连接或未打开')
    selectWritableReport(device)
    inputReportHandler = onInputReport
    device.addEventListener('inputreport', inputReportHandler)
    updateProgress('发送握手包...')
    await sendPacketData(buildHandshakePacket())
  } catch (error) {
    await handleError(error)
    throw error
  }
}

export function cancelSimpleUpgrade(): void {
  cleanup()
  currentState = STATE.IDLE
  firmwareBuffer = null
  fileReadAddr = FIRMWARE_OFFSET
}

export function getSimpleUpgradeState() {
  const sent = firmwareBuffer ? Math.max(fileReadAddr - FIRMWARE_OFFSET, 0) : 0
  const total = firmwareBuffer ? firmwareBuffer.length - FIRMWARE_OFFSET : 0
  return {
    state: currentState,
    progress: total > 0 ? Math.min(Math.floor((sent / total) * 100), 100) : 0,
    offset: fileReadAddr,
    total: firmwareBuffer?.length ?? 0,
  }
}
